import React from 'react'
import { cn } from '@/lib/utils'

type Tone = "sunset" | "creek" | "default" | "warm" | "sky" | "forest"

interface PhotoPlaceholderProps {
  caption?: string
  ratio?: string
  tone?: Tone
  style?: React.CSSProperties
  className?: string
}

const palettes: Record<string, [string, string]> = {
  warm:    ["#1c3a5c", "#f2dca6"],
  default: ["#1c3a5c", "#f2dca6"],
  sky:     ["#3a5a82", "#cfd9e3"],
  sunset:  ["#bf6a2a", "#f7d68d"],
  creek:   ["#2c4636", "#c9d3b6"],
  forest:  ["#2c4636", "#c9d3b6"],
}

export function PhotoPlaceholder({
  caption,
  ratio = "16/9",
  tone = "default",
  style,
  className,
}: PhotoPlaceholderProps) {
  const [dark, light] = palettes[tone] ?? palettes.default

  return (
    <div
      data-testid="photo-placeholder"
      role="img"
      aria-label={caption ?? "[photo placeholder]"}
      className={cn("relative w-full overflow-hidden rounded-sm", className)}
      style={{
        aspectRatio: ratio,
        background: `repeating-linear-gradient(135deg, ${dark}12 0 14px, ${dark}06 14px 28px), linear-gradient(180deg, ${light}55 0%, ${light}20 60%, ${dark}18 100%)`,
        backgroundColor: "var(--background-warm)",
        ...style,
      }}
    >
      {/* mountain silhouette */}
      <svg
        aria-hidden="true"
        viewBox="0 0 400 120"
        preserveAspectRatio="none"
        className="absolute bottom-0 left-0 w-full opacity-30"
        style={{ height: "42%" }}
      >
        <path d="M0 120 L0 70 L60 50 L120 80 L190 30 L260 70 L320 40 L400 70 L400 120 Z" fill={dark} />
        <path d="M0 120 L0 95 L80 85 L160 100 L260 80 L340 95 L400 88 L400 120 Z" fill={dark} opacity="0.55" />
      </svg>

      {/* sun glow */}
      <div
        aria-hidden="true"
        className="absolute rounded-full"
        style={{
          top: "20%",
          right: "18%",
          width: 64,
          height: 64,
          background: `radial-gradient(circle, ${light}cc, transparent 70%)`,
        }}
      />

      {caption && (
        <div
          className="hand absolute bottom-4 left-4"
          style={{
            fontSize: 20,
            color: dark,
            background: `${light}d0`,
            padding: "4px 10px",
            borderRadius: 6,
            transform: "rotate(-2deg)",
            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
          }}
        >
          {caption}
        </div>
      )}

      <div
        aria-hidden="true"
        className="absolute top-3 right-3.5 font-mono text-[10px] tracking-[0.18em] uppercase opacity-55"
        style={{ color: dark }}
      >
        PHOTO
      </div>
    </div>
  )
}
